import {AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ContractsFilterDto} from './contracts-filter.dto';

/**
 * UNIVAPLAT-3515: overlay filter for the contracts list, modelled on the admin dashboard
 * filter. The "contains" match is the backend's job; this component only collects values.
 * Hiding the show-deactivated / show-deleted checkboxes (`canSeeInactive`) is a convenience,
 * the API enforces.
 */
@Component({
    selector: 'cdss-contracts-filter',
    imports: [ReactiveFormsModule],
    standalone: true,
    templateUrl: './contracts-filter.component.html',
    styleUrl: './contracts-filter.component.scss'
})
export class ContractsFilterComponent implements AfterViewInit {

    form!: FormGroup;

    /** True for a general admin: shows the "Show deactivated / deleted" checkboxes. */
    @Input() canSeeInactive = false;

    @Output() apply = new EventEmitter<boolean>();
    @Output() cancel = new EventEmitter<{}>();
    @Output() filterChange = new EventEmitter<ContractsFilterDto>();

    private host = inject<ElementRef<HTMLElement>>(ElementRef);

    @ViewChild('firstField') firstField?: ElementRef<HTMLInputElement>;

    _filter!: ContractsFilterDto;

    get filter(): ContractsFilterDto {
        return this._filter;
    }

    @Input()
    set filter(value: ContractsFilterDto) {
        this._filter = value;
        this.initForm();
    }

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            title: [''],
            code: [''],
            serviceProvider: [''],
            showDeactivated: [false],
            showDeleted: [false],
        });
    }

    ngAfterViewInit(): void {
        // Keyboard users: move focus into the overlay when it opens.
        this.firstField?.nativeElement.focus();
    }

    initForm(): void {
        if (!this.filter) return;
        this.form.reset({
            title: this.filter.title || '',
            code: this.filter.code || '',
            serviceProvider: this.filter.serviceProvider || '',
            showDeactivated: this.filter.showDeactivated || false,
            showDeleted: this.filter.showDeleted || false,
        });
    }

    reset(): void {
        this.form.reset({
            title: '',
            code: '',
            serviceProvider: '',
            showDeactivated: false,
            showDeleted: false,
        });
        this.returnValue();
        this.apply.emit();
    }

    applyFilter(): void {
        this.returnValue();
        this.apply.emit();
    }

    returnValue(): void {
        const value = this.form.value;
        this.filter.title = (value.title || '').trim();
        this.filter.code = (value.code || '').trim();
        this.filter.serviceProvider = (value.serviceProvider || '').trim();
        // A contract admin never sees the checkboxes, so never send the flags for one.
        this.filter.showDeactivated = this.canSeeInactive && !!value.showDeactivated;
        this.filter.showDeleted = this.canSeeInactive && !!value.showDeleted;
        this.filterChange.emit(this.filter);
    }

    /** Keeps Tab / Shift+Tab inside the overlay (aria-modal): wraps at the first and last control. */
    trapFocus(event: KeyboardEvent): void {
        const focusable = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !this.host.nativeElement.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !this.host.nativeElement.contains(active))) {
            event.preventDefault();
            first.focus();
        }
    }

    close(): void {
        this.cancel.emit({});
    }
}
